/**
 * VitalsForm Component
 *
 * Form for capturing patient vital signs:
 * - Blood pressure
 * - Heart rate
 * - Temperature
 * - Respiratory rate
 * - Oxygen saturation
 * - Blood sugar
 * - Weight
 * - Height
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Save, Activity } from 'lucide-react';
import { Button } from '@/components/base/Button';
import { Input } from '@/components/base/Form/Input';
import { Label } from '@/components/base/label';
import type { VitalSigns } from '@/lib/types/consultation';

/**
 * Vitals form schema with validation
 */
const vitalsFormSchema = z.object({
  bloodPressure: z
    .string()
    .regex(/^\d{2,3}\/\d{2,3}$/, 'Format: systolic/diastolic (e.g., 120/80)')
    .optional()
    .or(z.literal(''))
    .refine(
      (val) => {
        if (!val || val.trim() === '') return true;
        const match = val.trim().match(/^(\d+)\/(\d+)$/);
        if (!match) return true; // format error already caught above
        const systolic = parseInt(match[1], 10);
        const diastolic = parseInt(match[2], 10);
        return (
          systolic >= 50 &&
          systolic <= 250 &&
          diastolic >= 30 &&
          diastolic <= 180 &&
          systolic > diastolic
        );
      },
      { message: 'Systolic: 50–250, diastolic: 30–180, systolic must exceed diastolic' }
    ),
  heartRate: z
    .number()
    .min(30, 'Heart rate must be at least 30 bpm')
    .max(220, 'Heart rate must be at most 220 bpm')
    .optional()
    .or(z.nan()),
  temperature: z
    .number()
    .min(90, 'Temperature must be at least 90°F')
    .max(110, 'Temperature must be at most 110°F')
    .optional()
    .or(z.nan()),
  respiratoryRate: z
    .number()
    .min(5, 'Respiratory rate must be at least 5 breaths/min')
    .max(60, 'Respiratory rate must be at most 60 breaths/min')
    .optional()
    .or(z.nan()),
  oxygenSaturation: z
    .number()
    .min(50, 'SpO2 must be at least 50%')
    .max(100, 'SpO2 cannot exceed 100%')
    .optional()
    .or(z.nan()),
  bloodSugar: z
    .number()
    .min(20, 'Blood sugar must be at least 20 mg/dL')
    .max(700, 'Blood sugar must be at most 700 mg/dL')
    .optional()
    .or(z.nan()),
  weight: z
    .number()
    .min(1, 'Weight must be at least 1 kg')
    .max(400, 'Weight must be at most 400 kg')
    .optional()
    .or(z.nan()),
  height: z
    .number()
    .min(30, 'Height must be at least 30 cm')
    .max(300, 'Height must be at most 300 cm')
    .optional()
    .or(z.nan()),
  hemoglobin: z
    .number()
    .min(2, 'Hemoglobin must be at least 2 g/dL')
    .max(25, 'Hemoglobin must be at most 25 g/dL')
    .optional()
    .or(z.nan()),
  cholesterol: z
    .number()
    .min(50, 'Cholesterol must be at least 50 mg/dL')
    .max(500, 'Cholesterol must be at most 500 mg/dL')
    .optional()
    .or(z.nan()),
});

type VitalsFormData = z.infer<typeof vitalsFormSchema>;

/**
 * Props for VitalsForm component
 */
export interface VitalsFormProps {
  /** Initial vitals data */
  initialVitals?: VitalSigns;
  /** Callback when form is submitted */
  onSubmit: (vitals: VitalSigns) => void;
  /** Callback when form is cleared */
  onClear?: () => void;
  /** Disable form */
  disabled?: boolean;
  /** Loading state */
  isLoading?: boolean;
}

/**
 * Convert form data to VitalSigns
 */
function formDataToVitals(data: VitalsFormData): VitalSigns {
  return {
    bloodPressure: data.bloodPressure || undefined,
    heartRate: !isNaN(data.heartRate!) ? data.heartRate : undefined,
    temperature: !isNaN(data.temperature!) ? data.temperature : undefined,
    respiratoryRate: !isNaN(data.respiratoryRate!) ? data.respiratoryRate : undefined,
    oxygenSaturation: !isNaN(data.oxygenSaturation!) ? data.oxygenSaturation : undefined,
    bloodSugar: !isNaN(data.bloodSugar!) ? data.bloodSugar : undefined,
    weight: !isNaN(data.weight!) ? data.weight : undefined,
    height: !isNaN(data.height!) ? data.height : undefined,
    hemoglobin: !isNaN(data.hemoglobin!) ? data.hemoglobin : undefined,
    cholesterol: !isNaN(data.cholesterol!) ? data.cholesterol : undefined,
  };
}

/**
 * VitalsForm Component
 */
export function VitalsForm({
  initialVitals,
  onSubmit,
  onClear,
  disabled = false,
  isLoading = false,
}: VitalsFormProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<VitalsFormData>({
    resolver: zodResolver(vitalsFormSchema),
    defaultValues: {
      bloodPressure: initialVitals?.bloodPressure || '',
      heartRate: initialVitals?.heartRate || NaN,
      temperature: initialVitals?.temperature || NaN,
      respiratoryRate: initialVitals?.respiratoryRate || NaN,
      oxygenSaturation: initialVitals?.oxygenSaturation || NaN,
      bloodSugar: initialVitals?.bloodSugar || NaN,
      weight: initialVitals?.weight || NaN,
      height: initialVitals?.height || NaN,
      hemoglobin: initialVitals?.hemoglobin || NaN,
      cholesterol: initialVitals?.cholesterol || NaN,
    },
  });

  /**
   * Handle form submission
   */
  const handleFormSubmit = (data: VitalsFormData) => {
    const vitals = formDataToVitals(data);
    onSubmit(vitals);
  };

  /**
   * Handle form clear
   */
  const handleClear = () => {
    reset({
      bloodPressure: '',
      heartRate: NaN,
      temperature: NaN,
      respiratoryRate: NaN,
      oxygenSaturation: NaN,
      bloodSugar: NaN,
      weight: NaN,
      height: NaN,
      hemoglobin: NaN,
      cholesterol: NaN,
    });
    onClear?.();
  };

  // Prevent scientific notation characters (e/E/+/-) in numeric vitals inputs.
  // HTML type="number" allows these by default but they are invalid for clinical measurements.
  const blockInvalidNumericKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      {/* Blood Pressure */}
      <div>
        <Label htmlFor="bloodPressure" className="flex items-center gap-2">
          Blood Pressure
          <span className="text-xs text-muted-foreground">(mmHg)</span>
        </Label>
        <Input
          id="bloodPressure"
          {...register('bloodPressure')}
          placeholder="120/80"
          disabled={disabled}
          className={errors.bloodPressure ? 'border-destructive' : ''}
        />
        {errors.bloodPressure && (
          <p className="mt-1 text-sm text-destructive">{errors.bloodPressure.message}</p>
        )}
      </div>

      {/* Heart Rate */}
      <div>
        <Label htmlFor="heartRate" className="flex items-center gap-2">
          Heart Rate
          <span className="text-xs text-muted-foreground">(bpm)</span>
        </Label>
        <Input
          id="heartRate"
          type="number"
          {...register('heartRate', { valueAsNumber: true })}
          placeholder="72"
          onKeyDown={blockInvalidNumericKey}
          disabled={disabled}
          className={errors.heartRate ? 'border-destructive' : ''}
        />
        {errors.heartRate && (
          <p className="mt-1 text-sm text-destructive">{errors.heartRate.message}</p>
        )}
      </div>

      {/* Temperature */}
      <div>
        <Label htmlFor="temperature" className="flex items-center gap-2">
          Temperature
          <span className="text-xs text-muted-foreground">(°F)</span>
        </Label>
        <Input
          id="temperature"
          type="number"
          step="0.1"
          {...register('temperature', { valueAsNumber: true })}
          placeholder="101.2"
          onKeyDown={blockInvalidNumericKey}
          disabled={disabled}
          className={errors.temperature ? 'border-destructive' : ''}
        />
        {errors.temperature && (
          <p className="mt-1 text-sm text-destructive">{errors.temperature.message}</p>
        )}
      </div>

      {/* Respiratory Rate */}
      <div>
        <Label htmlFor="respiratoryRate" className="flex items-center gap-2">
          Respiratory Rate
          <span className="text-xs text-muted-foreground">(breaths/min)</span>
        </Label>
        <Input
          id="respiratoryRate"
          type="number"
          {...register('respiratoryRate', { valueAsNumber: true })}
          placeholder="16"
          onKeyDown={blockInvalidNumericKey}
          disabled={disabled}
          className={errors.respiratoryRate ? 'border-destructive' : ''}
        />
        {errors.respiratoryRate && (
          <p className="mt-1 text-sm text-destructive">{errors.respiratoryRate.message}</p>
        )}
      </div>

      {/* Oxygen Saturation */}
      <div>
        <Label htmlFor="oxygenSaturation" className="flex items-center gap-2">
          Oxygen Saturation
          <span className="text-xs text-muted-foreground">(%)</span>
        </Label>
        <Input
          id="oxygenSaturation"
          type="number"
          {...register('oxygenSaturation', { valueAsNumber: true })}
          placeholder="98"
          onKeyDown={blockInvalidNumericKey}
          disabled={disabled}
          className={errors.oxygenSaturation ? 'border-destructive' : ''}
        />
        {errors.oxygenSaturation && (
          <p className="mt-1 text-sm text-destructive">{errors.oxygenSaturation.message}</p>
        )}
      </div>

      {/* Blood Sugar */}
      <div>
        <Label htmlFor="bloodSugar" className="flex items-center gap-2">
          Blood Sugar
          <span className="text-xs text-muted-foreground">(mg/dL)</span>
        </Label>
        <Input
          id="bloodSugar"
          type="number"
          {...register('bloodSugar', { valueAsNumber: true })}
          placeholder="100"
          onKeyDown={blockInvalidNumericKey}
          disabled={disabled}
          className={errors.bloodSugar ? 'border-destructive' : ''}
        />
        {errors.bloodSugar && (
          <p className="mt-1 text-sm text-destructive">{errors.bloodSugar.message}</p>
        )}
      </div>

      {/* Weight */}
      <div>
        <Label htmlFor="weight" className="flex items-center gap-2">
          Weight
          <span className="text-xs text-muted-foreground">(kg)</span>
        </Label>
        <Input
          id="weight"
          type="number"
          step="0.1"
          {...register('weight', { valueAsNumber: true })}
          placeholder="70.5"
          onKeyDown={blockInvalidNumericKey}
          disabled={disabled}
          className={errors.weight ? 'border-destructive' : ''}
        />
        {errors.weight && <p className="mt-1 text-sm text-destructive">{errors.weight.message}</p>}
      </div>

      {/* Height */}
      <div>
        <Label htmlFor="height" className="flex items-center gap-2">
          Height
          <span className="text-xs text-muted-foreground">(cm)</span>
        </Label>
        <Input
          id="height"
          type="number"
          step="0.1"
          {...register('height', { valueAsNumber: true })}
          placeholder="170"
          onKeyDown={blockInvalidNumericKey}
          disabled={disabled}
          className={errors.height ? 'border-destructive' : ''}
        />
        {errors.height && <p className="mt-1 text-sm text-destructive">{errors.height.message}</p>}
      </div>

      {/* Hemoglobin */}
      <div>
        <Label htmlFor="hemoglobin" className="flex items-center gap-2">
          Hemoglobin
          <span className="text-xs text-muted-foreground">(g/dL)</span>
        </Label>
        <Input
          id="hemoglobin"
          type="number"
          step="0.1"
          {...register('hemoglobin', { valueAsNumber: true })}
          placeholder="13.5"
          onKeyDown={blockInvalidNumericKey}
          disabled={disabled}
          className={errors.hemoglobin ? 'border-destructive' : ''}
        />
        {errors.hemoglobin && (
          <p className="mt-1 text-sm text-destructive">{errors.hemoglobin.message}</p>
        )}
      </div>

      {/* Cholesterol */}
      <div>
        <Label htmlFor="cholesterol" className="flex items-center gap-2">
          Cholesterol
          <span className="text-xs text-muted-foreground">(mg/dL)</span>
        </Label>
        <Input
          id="cholesterol"
          type="number"
          {...register('cholesterol', { valueAsNumber: true })}
          placeholder="180"
          onKeyDown={blockInvalidNumericKey}
          disabled={disabled}
          className={errors.cholesterol ? 'border-destructive' : ''}
        />
        {errors.cholesterol && (
          <p className="mt-1 text-sm text-destructive">{errors.cholesterol.message}</p>
        )}
      </div>

      {/* Form Actions */}
      <div className="flex items-center gap-2 pt-4">
        <Button type="submit" disabled={disabled || isLoading || !isDirty} className="flex-1">
          <Save className="mr-2 h-4 w-4" />
          {isLoading ? 'Saving...' : 'Save Vitals'}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={handleClear}
          disabled={disabled || isLoading || !isDirty}
        >
          Clear
        </Button>
      </div>

      {/* Helper Text */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Activity className="h-3 w-3" />
        <span>All fields are optional. Fill in available vitals.</span>
      </div>
    </form>
  );
}
